/*
 * Matching a site to an address on its client's Housecall Pro customer.
 *
 * The two were typed by different people into different systems, so "4502
 * 98th Street" here is "4502 98th St" there. A match is a suggestion a person
 * confirms, never a link made on its own: two addresses on one street read
 * alike once they are normalized, and a job sent to the wrong one sends a
 * crew there.
 */

import type { HcpAddressDto } from '../dto/housecall-pro.dto.js'

/** An address, in the fields both sides have. */
export type StreetAddress = Pick<
  HcpAddressDto,
  'street' | 'city' | 'state' | 'zip'
>

/** The words people abbreviate, by their usual abbreviation. */
const ABBREVIATIONS = {
  street: 'st',
  avenue: 'ave',
  drive: 'dr',
  road: 'rd',
  boulevard: 'blvd',
  lane: 'ln',
  court: 'ct',
  place: 'pl',
  parkway: 'pkwy',
  circle: 'cir',
  highway: 'hwy',
  terrace: 'ter',
  trail: 'trl',
  north: 'n',
  south: 's',
  east: 'e',
  west: 'w',
  suite: 'ste',
} as const

type StreetWord = keyof typeof ABBREVIATIONS

/** An own key of the abbreviation list, not a property of every object. */
function isStreetWord(word: string): word is StreetWord {
  return Object.prototype.hasOwnProperty.call(ABBREVIATIONS, word)
}

/** "4502 98th Street." and "4502  98th st" both read "4502 98th st". */
export function normalizeStreet(street: string): string {
  return street
    .toLowerCase()
    .replace(/[.,#]/g, ' ')
    .split(/\s+/)
    .filter((word) => word !== '')
    .map((word) => (isStreetWord(word) ? ABBREVIATIONS[word] : word))
    .join(' ')
}

/** The first five digits: a ZIP+4 and its ZIP are the same place. */
function zip5(zip: string | null): string | null {
  const digits = zip?.match(/\d{5}/)?.[0]
  return digits ?? null
}

/**
 * Every address that reads as the site: the same street once normalized, and
 * the same ZIP where both have one.
 *
 * More than one is a choice a person has to make. Callers that only want the
 * unambiguous case use `matchingHcpAddress`, which returns null for both
 * "none" and "several" — those two are not the same, and treating them as
 * the same is how a second copy of the address gets created.
 */
export function matchingHcpAddresses<A extends StreetAddress & { id: string }>(
  site: StreetAddress,
  addresses: readonly A[],
): A[] {
  if (site.street === null || site.street.trim() === '') return []
  const street = normalizeStreet(site.street)
  const zip = zip5(site.zip)
  return addresses.filter((address) => {
    if (address.street === null) return false
    if (normalizeStreet(address.street) !== street) return false
    const theirs = zip5(address.zip)
    return zip === null || theirs === null || zip === theirs
  })
}

/**
 * The one address that is the site's. Null when none matches, or when more
 * than one does, which a person has to settle.
 */
export function matchingHcpAddress<A extends StreetAddress & { id: string }>(
  site: StreetAddress,
  addresses: readonly A[],
): A | null {
  const matches = matchingHcpAddresses(site, addresses)
  return matches.length === 1 ? (matches[0] ?? null) : null
}

/** One line, as a link to the address is shown: "4502 98th St, Lubbock". */
export function hcpAddressLabel(address: StreetAddress): string {
  const place = [address.city, address.state]
    .filter((part): part is string => part !== null && part.trim() !== '')
    .join(', ')
  const street = address.street?.trim() ?? ''
  if (street !== '' && address.city !== null && address.city.trim() !== '') {
    return `${street}, ${address.city.trim()}`
  }
  if (street !== '') return street
  if (place !== '') return place
  return address.zip ?? 'An address with no street'
}
